// The Salish Sea page's region model: the corridor from Puget Sound to
// Desolation Sound, cut into named areas.
//
// Why areas at all, rather than one box for the whole thing: the shared
// weather code asks the per-waypoint NWS sources about at most
// MAX_FORECAST_WAYPOINTS (5) points and renders at most MAX_ZONES (6) marine
// zones. This corridor holds roughly ten NWS marine zones plus eight ECCC
// areas, so a single view can only ever be an overview -- five points spread
// over three degrees says something about each end and little about the
// middle. The areas are where the detail lives; 'all' is the way in.
//
// Each area's `anchors` are the points handed to useMapWeather, and they
// decide which NWS grid cells, forecast zones and marine zones the page
// reports -- so they sit in the main channels, on water, not on the beach a
// place name would put them. Five per area is deliberate: at exactly the cap,
// lookupPoints samples none of them away.
//
// Anchors north of the border are not wasted. `/points` 404s outside the NWS
// domain and nwsPointMeta turns that into null, which is what stops a
// Canadian anchor from spending gridpoint, alert and zone-probe requests --
// the Canadian half of the map is served by the bbox-driven ECCC sources
// instead.
//
// Bounds are the app's [[minLon, minLat], [maxLon, maxLat]] convention. Every
// one of them is far inside GeoBounds' weather cap even after padBounds grows
// it 0.4 degrees on each side, so the oversize gate can never trip on this
// page -- salishAreas.test.mjs pins that rather than leaving it as a claim.

export const SALISH_AREAS = [
  {
    slug: 'all',
    label: 'Entire region',
    blurb: 'Puget Sound to Desolation Sound.',
    bounds: [[-125.35, 46.95], [-122.15, 50.35]],
    anchors: [
      { id: 'elliott-bay', name: 'Elliott Bay', lat: 47.60, lon: -122.40 },
      { id: 'admiralty-inlet', name: 'Admiralty Inlet', lat: 48.12, lon: -122.76 },
      { id: 'haro-strait', name: 'Haro Strait', lat: 48.50, lon: -123.15 },
      { id: 'georgia-strait', name: 'Strait of Georgia', lat: 49.34, lon: -123.72 },
      { id: 'desolation-sound', name: 'Desolation Sound', lat: 50.05, lon: -124.75 },
    ],
  },
  {
    slug: 'puget-sound',
    label: 'Puget Sound',
    blurb: 'Olympia and the South Sound north to Possession Sound.',
    bounds: [[-123.25, 46.95], [-122.15, 48.05]],
    anchors: [
      { id: 'budd-inlet', name: 'Budd Inlet', lat: 47.08, lon: -122.90 },
      { id: 'tacoma-narrows', name: 'Tacoma Narrows', lat: 47.27, lon: -122.55 },
      { id: 'elliott-bay', name: 'Elliott Bay', lat: 47.60, lon: -122.40 },
      { id: 'hood-canal', name: 'Hood Canal', lat: 47.64, lon: -122.87 },
      { id: 'possession-sound', name: 'Possession Sound', lat: 47.93, lon: -122.33 },
    ],
  },
  {
    slug: 'admiralty-whidbey',
    label: 'Admiralty Inlet & Whidbey',
    blurb: 'Port Townsend, Whidbey Island and Deception Pass.',
    bounds: [[-123.00, 47.85], [-122.25, 48.55]],
    anchors: [
      { id: 'port-townsend', name: 'Port Townsend', lat: 48.11, lon: -122.76 },
      { id: 'admiralty-inlet', name: 'Admiralty Inlet', lat: 48.16, lon: -122.65 },
      { id: 'saratoga-passage', name: 'Saratoga Passage', lat: 48.16, lon: -122.50 },
      { id: 'skagit-bay', name: 'Skagit Bay', lat: 48.33, lon: -122.48 },
      { id: 'deception-pass', name: 'Deception Pass', lat: 48.41, lon: -122.64 },
    ],
  },
  {
    slug: 'san-juans',
    label: 'San Juan Islands',
    blurb: 'Haro and Rosario Straits, Boundary Pass and Bellingham Bay.',
    bounds: [[-123.40, 48.30], [-122.45, 48.90]],
    anchors: [
      { id: 'haro-strait', name: 'Haro Strait', lat: 48.55, lon: -123.25 },
      { id: 'friday-harbor', name: 'Friday Harbor', lat: 48.54, lon: -123.01 },
      { id: 'rosario-strait', name: 'Rosario Strait', lat: 48.55, lon: -122.75 },
      { id: 'boundary-pass', name: 'Boundary Pass', lat: 48.75, lon: -123.10 },
      { id: 'bellingham-bay', name: 'Bellingham Bay', lat: 48.68, lon: -122.55 },
    ],
  },
  {
    slug: 'juan-de-fuca',
    label: 'Strait of Juan de Fuca',
    blurb: 'Neah Bay and the entrance east to Dungeness and Victoria.',
    bounds: [[-124.85, 48.00], [-122.85, 48.70]],
    anchors: [
      { id: 'neah-bay', name: 'Neah Bay', lat: 48.37, lon: -124.60 },
      { id: 'port-angeles', name: 'Port Angeles', lat: 48.16, lon: -123.44 },
      { id: 'race-rocks', name: 'Race Rocks', lat: 48.30, lon: -123.53 },
      { id: 'victoria', name: 'Victoria', lat: 48.38, lon: -123.37 },
      { id: 'dungeness', name: 'Dungeness', lat: 48.20, lon: -123.10 },
    ],
  },
  {
    slug: 'gulf-islands',
    label: 'Gulf Islands & S. Georgia',
    blurb: 'Active Pass and Nanaimo across to Vancouver and Howe Sound.',
    bounds: [[-124.25, 48.65], [-122.90, 49.55]],
    anchors: [
      { id: 'active-pass', name: 'Active Pass', lat: 48.87, lon: -123.32 },
      { id: 'nanaimo', name: 'Nanaimo', lat: 49.20, lon: -123.90 },
      { id: 'halibut-bank', name: 'Halibut Bank', lat: 49.34, lon: -123.72 },
      { id: 'english-bay', name: 'English Bay', lat: 49.30, lon: -123.25 },
      { id: 'howe-sound', name: 'Howe Sound', lat: 49.45, lon: -123.35 },
    ],
  },
  {
    slug: 'desolation',
    label: 'N. Georgia & Desolation Sound',
    blurb: 'Hornby Island north through Powell River to Desolation Sound.',
    bounds: [[-125.35, 49.35], [-123.95, 50.35]],
    anchors: [
      { id: 'hornby-island', name: 'Hornby Island', lat: 49.54, lon: -124.99 },
      { id: 'comox', name: 'Comox', lat: 49.68, lon: -124.85 },
      { id: 'powell-river', name: 'Powell River', lat: 49.83, lon: -124.60 },
      { id: 'discovery-passage', name: 'Discovery Passage', lat: 50.03, lon: -125.22 },
      { id: 'desolation-sound', name: 'Desolation Sound', lat: 50.12, lon: -124.71 },
    ],
  },
];

export const DEFAULT_AREA_SLUG = 'all';

export function isAreaSlug(slug) {
  return SALISH_AREAS.some((area) => area.slug === slug);
}

// Always an area, never null: an unknown or absent slug is the default view,
// not an error page. The URL is never rewritten to say so -- absent means
// "the default", the way TripsCalendarSection treats a missing ?month=.
export function areaBySlug(slug) {
  return (
    SALISH_AREAS.find((area) => area.slug === slug) ||
    SALISH_AREAS.find((area) => area.slug === DEFAULT_AREA_SLUG)
  );
}
